import { useSyncExternalStore } from 'react'
import type { Role, View } from '../domain/models'

interface AppState {
  role: Role
  view: View
  toast: { title: string; message: string } | null
}

const paths: Record<View, string> = {
  yard: '/yard',
  resources: '/materials',
  fuel: '/fuel',
  costs: '/costs',
  history: '/history',
  operator: '/o/home',
  settings: '/settings',
  procurement: '/procurement',
  workorders: '/works',
  imports: '/imports',
  dashboard: '/overview',
  fleet: '/fleet',
  maintenance: '/maintenance',
  requests: '/requests',
  schedule: '/schedule',
  operators: '/operators',
  map: '/map',
  clients: '/clients',
  documents: '/documents',
  mechanic: '/m/work',
  client: '/client',
  profile: '/profile',
  search: '/search',
  mechanicFleet: '/m/fleet',
  mechanicMaterials: '/m/materials',
  mechanicEvents: '/m/events',
  operatorMessages: '/o/messages',
  assistant: '/assistant',
  notifications: '/notifications',
}

const views = Object.keys(paths) as View[]
const pathToView = new Map(views.map((view) => [paths[view], view]))
pathToView.set('/', 'dashboard')
pathToView.set('/mechanic', 'mechanic')
pathToView.set('/operator', 'operator')
pathToView.set('/login', 'dashboard')
pathToView.set('/workorders', 'workorders')
pathToView.set('/resources', 'resources')
const roleDefaults: Record<Role, View> = {
  owner: 'yard',
  manager: 'dashboard',
  dispatcher: 'dashboard',
  operator: 'operator',
  mechanic: 'mechanic',
  client: 'client',
}

const readView = (): View =>
  window.location.pathname.startsWith('/m/fleet/')
    ? 'mechanicFleet'
    : window.location.pathname.startsWith('/fleet/')
      ? 'fleet'
      : (pathToView.get(window.location.pathname) ?? 'dashboard')
const initialView = readView()
let state: AppState = {
  role:
    initialView === 'client'
      ? 'client'
      : ['mechanic', 'mechanicFleet', 'mechanicMaterials', 'mechanicEvents'].includes(initialView)
        ? 'mechanic'
        : ['operator', 'operatorMessages'].includes(initialView)
          ? 'operator'
          : 'manager',
  view: initialView,
  toast: null,
}
const listeners = new Set<() => void>()
let toastTimer: number | undefined

const emit = () => listeners.forEach((listener) => listener())
const update = (patch: Partial<AppState>) => {
  state = { ...state, ...patch }
  emit()
}

export const appStore = {
  subscribe(listener: () => void) {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },
  getSnapshot: () => state,
  navigate(view: View, params?: Record<string, string>, replace = false) {
    if (state.view === view && !params && window.location.pathname === paths[view]) return
    window.history[replace ? 'replaceState' : 'pushState'](
      {},
      '',
      paths[view] + (params ? '?' + new URLSearchParams(params).toString() : ''),
    )
    update({ view })
  },
  setRole(role: Role) {
    const view = roleDefaults[role]
    window.history.pushState({}, '', paths[view])
    update({ role, view })
  },
  showLogin() {
    window.history.replaceState({}, '', '/login')
    update({ view: 'dashboard' })
  },
  showToast(title: string, message: string) {
    window.clearTimeout(toastTimer)
    update({ toast: { title, message } })
    toastTimer = window.setTimeout(() => update({ toast: null }), 3600)
  },
}

window.addEventListener('popstate', () => update({ view: readView() }))

export const useAppState = () => useSyncExternalStore(appStore.subscribe, appStore.getSnapshot)
